import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSpring } from 'react-spring';
import styled from 'styled-components';

import { FontAwesomeIcon } from 'atoms/icons';
import { Horizontal, SBHorizontal, Vertical } from 'atoms/layout/flex';
import { LabelM } from 'atoms/typography';
import { Box } from 'atoms/ui/Box';
import { Gauge } from 'atoms/ui/Gauge';
import { glossary } from 'lib/glossary';

const Root = styled(Box)`
  width: 100%;
`;
const HorizontalWithSpace = styled(SBHorizontal)`
  flex-wrap: wrap;
`;
const UppercaseLabelM = styled(LabelM)`
  text-transform: uppercase;
`;

type Props = {
  title?: string;
  description: string;
  isCompleted: boolean;
  progression?: number | null;
  target?: number | null;
  claimButton: ReactNode;
  rewardPreview: ReactNode;
  disableClaim?: boolean;
};
export const Challenge = ({
  title,
  description,
  isCompleted,
  progression,
  target,
  claimButton,
  rewardPreview,
  disableClaim,
}: Props) => {
  const showProgressBar =
    typeof progression === 'number' && typeof target === 'number';
  const [{ percentage }] = useSpring(() => ({
    from: { percentage: '0%' },
    to: {
      percentage: `${((progression || 0) * 100) / (target || 1)}%`,
    },
  }));

  return (
    <Root>
      <Vertical gap={1.5}>
        {title && (
          <LabelM as="span" bold>
            {title}
          </LabelM>
        )}
        <Vertical gap={0.5}>
          <Horizontal>
            <FontAwesomeIcon
              icon={faCheckCircle}
              color={
                isCompleted ? 'var(--c-score-high)' : 'var(--c-neutral-400)'
              }
            />
            <LabelM as="span">{description}</LabelM>
          </Horizontal>
          {showProgressBar && (
            <Vertical gap={1}>
              <Horizontal>
                <Gauge percentage={percentage} color="var(--c-score-high)" />
                <LabelM as="p">
                  <span
                    style={{
                      color: isCompleted
                        ? 'var(--c-score-high)'
                        : 'var(--c-neutral-900)',
                    }}
                  >
                    {progression}
                  </span>
                  /{target}
                </LabelM>
              </Horizontal>
              <UppercaseLabelM as="p" color="var(--c-score-high)">
                <FormattedMessage {...glossary.rewards} />
              </UppercaseLabelM>
            </Vertical>
          )}
        </Vertical>
        <Vertical gap={1}>
          {!showProgressBar && (
            <UppercaseLabelM as="p" color="var(--c-score-high)">
              <FormattedMessage {...glossary.rewards} />
            </UppercaseLabelM>
          )}
          <HorizontalWithSpace>
            {rewardPreview}
            {!disableClaim && claimButton}
          </HorizontalWithSpace>
        </Vertical>
      </Vertical>
    </Root>
  );
};
